import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Container,
  useTheme,
  Paper,
  Breadcrumbs,
  Link,
  IconButton,
  Button,
  Chip
} from '@mui/material';
import { useUser } from "@clerk/clerk-react";
import { tokens } from '../../theme';
import { useSettings } from '../../contexts/SettingsContext';
import { getCacheItem, setCacheItem, removeCacheItem, ONE_DAY } from '../../utils/cache';
import KeeperTeamTile from './KeeperTeamTile';
import KeeperTeamDetailModal from './KeeperTeamDetailModal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';


const KeeperTeams = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useUser();
  const { scoringType, isSuperflex } = useSettings();
  
  const [leagueDetails, setLeagueDetails] = useState(null);
  const [users, setUsers] = useState({});
  const [rosters, setRosters] = useState([]);
  const [keeperData, setKeeperData] = useState([]);
  // The ADP season the backend actually priced against, echoed back in the
  // keeper-values response. Displayed rather than hardcoded so the UI cannot
  // claim one season while showing another's numbers.
  const [adpSeason, setAdpSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScoringType, setSelectedScoringType] = useState('');

  const [refreshing, setRefreshing] = useState(false);
  const [selectedTeamModal, setSelectedTeamModal] = useState(null);

  // Helper function to get API headers with authentication
  const getApiHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
    };
    
    if (user) {
      headers["X-Clerk-User-Id"] = user.id || "unknown";
      headers["X-Clerk-Email"] = user.primaryEmailAddress?.emailAddress || "";
      headers["X-Clerk-Username"] = user.username || "";
    }
    
    return headers;
  };

  // Determine initial scoring type
  useEffect(() => {
    // console.log('[KeeperTeams.fetchKeeperData] useEffect triggered for scoring type setup');
    if (scoringType) { // Only depend on the source from context
      let mappedScoringType = scoringType;
      if (isSuperflex) {
        mappedScoringType = scoringType === 'ppr' ? 'superflex_ppr' : 
                            scoringType === 'half_ppr' ? 'superflex_half_ppr' : 
                            scoringType === 'standard' ? 'superflex_standard' : 'superflex_ppr';
      }
      // Set it only if it's different to avoid re-renders
      if (mappedScoringType !== selectedScoringType) {
        setSelectedScoringType(mappedScoringType);
      }
    }
  }, [scoringType, isSuperflex, selectedScoringType]);



  const fetchKeeperData = async () => {
    // console.log('[KeeperTeams.fetchKeeperData] Starting fetch process');
    if (!selectedScoringType) return;
    
    try {
      setRefreshing(true);
      setError(null);

      const headers = getApiHeaders();
      
      // Create cache keys for this league and scoring type
      const cacheKeyBase = `keeper-data-${leagueId}-${selectedScoringType}`;
      const leagueCacheKey = `${cacheKeyBase}-league`;
      const usersCacheKey = `${cacheKeyBase}-users`;
      const rostersCacheKey = `${cacheKeyBase}-rosters`;
      const keeperValuesCacheKey = `${cacheKeyBase}-keeper-values`;
      const playersCacheKey = `${cacheKeyBase}-players`;
      
      // Try to get cached data first
      const cachedLeague = getCacheItem(leagueCacheKey);
      const cachedUsers = getCacheItem(usersCacheKey);
      const cachedRosters = getCacheItem(rostersCacheKey);
      const cachedKeeperValues = getCacheItem(keeperValuesCacheKey);
      const cachedPlayers = getCacheItem(playersCacheKey);
      
      // If we have all cached data, use it
      if (cachedLeague && cachedUsers && cachedRosters && cachedKeeperValues && cachedPlayers) {
        // console.log('[KeeperTeams.fetchKeeperData] Using cached data - skipping API calls');
        setLeagueDetails(cachedLeague);
        setUsers(cachedUsers);
        setRosters(cachedRosters);
        setKeeperData(cachedKeeperValues);
        window.playersLookup = cachedPlayers;
        setRefreshing(false);
        return;
      }
      
      // console.log('[KeeperTeams.fetchKeeperData] Cache miss - fetching fresh data from APIs');
      
      // First fetch league from your API to get basic info
      // console.log('[KeeperTeams.fetchKeeperData] Starting internal league API fetch');
      const internalLeagueResponse = await fetch(`/api/leagues/${leagueId}`, { headers });
      if (!internalLeagueResponse.ok) {
        throw new Error('Failed to fetch league from internal API');
      }
      
      const internalLeagueData = await internalLeagueResponse.json();
      // console.log('[KeeperTeams.fetchKeeperData] Internal league API response:', {
      //   success: true,
      //   leagueId: internalLeagueData.id || 'unknown',
      //   externalLeagueId: internalLeagueData.external_league_id || 'unknown',
      //   keeperSettings: {
      //     keeper_max_years_kept: internalLeagueData.keeper_max_years_kept,
      //     keeper_highest_round: internalLeagueData.keeper_highest_round,
      //     keeper_round_cost: internalLeagueData.keeper_round_cost,
      //     keeper_undrafted_round: internalLeagueData.keeper_undrafted_round
      //   }
      // });
      
      // Enhanced production debugging for league settings
      const enhancedLeagueData = {
        ...internalLeagueData,
        keeper_max_years_kept: internalLeagueData.keeper_max_years_kept ?? 2,
        keeper_highest_round: internalLeagueData.keeper_highest_round ?? 3,
        keeper_round_cost: internalLeagueData.keeper_round_cost ?? 2,
        keeper_undrafted_round: internalLeagueData.keeper_undrafted_round ?? 10
      };
      
      setLeagueDetails(enhancedLeagueData);
      setCacheItem(leagueCacheKey, enhancedLeagueData, ONE_DAY);
      
      const externalLeagueId = internalLeagueData.external_league_id;
      if (!externalLeagueId) {
        throw new Error('League does not have an external_league_id');
      }

      // Fetch both users and rosters from Sleeper API
      // console.log('[KeeperTeams.fetchKeeperData] Starting Sleeper API fetches (users & rosters)');
      const [usersResponse, rostersResponse] = await Promise.allSettled([
        fetch(`https://api.sleeper.app/v1/league/${externalLeagueId}/users`),
        fetch(`https://api.sleeper.app/v1/league/${externalLeagueId}/rosters`)
      ]);

      // console.log('[KeeperTeams.fetchKeeperData] Sleeper API responses:', {
      //   users: {
      //     status: usersResponse.status,
      //     ok: usersResponse.status === 'fulfilled' ? usersResponse.value.ok : false
      //   },
      //   rosters: {
      //     status: rostersResponse.status,
      //     ok: rostersResponse.status === 'fulfilled' ? rostersResponse.value.ok : false
      //   }
      // });

      // Handle users
      let usersMap = {};
      if (usersResponse.status === 'fulfilled' && usersResponse.value.ok) {
        const usersData = await usersResponse.value.json();
        usersData.forEach(user => {
          usersMap[user.user_id] = user;
        });
        setUsers(usersMap);
        setCacheItem(usersCacheKey, usersMap, ONE_DAY);
      } else {
        setUsers({});
        setCacheItem(usersCacheKey, {}, ONE_DAY);
      }

      // Handle rosters
      let rostersData = [];
      if (rostersResponse.status === 'fulfilled' && rostersResponse.value.ok) {
        rostersData = await rostersResponse.value.json();
        setRosters(rostersData || []);
        setCacheItem(rostersCacheKey, rostersData || [], ONE_DAY);
      } else {
        setRosters([]);
        setCacheItem(rostersCacheKey, [], ONE_DAY);
      }

      // Optionally fetch keeper values from your API (this might be empty if not set up yet)
      let keeperResults = [];
      try {
        // No `season` param: the backend defaults to the current NFL season
        // (models/season.py), which is now kept current by the scheduled ADP
        // sync. This used to be pinned to 2025 because FantasyPros ADP had been
        // frozen since 2025-08-23; ADP now comes from Fantasy Football
        // Calculator and refreshes daily, so pinning here would re-freeze the
        // UI at a stale season no matter what the pipeline ingests.
        const keeperUrl = `/api/leagues/${leagueId}/keeper-values/?scoring_type=${selectedScoringType}`;
        // console.log('[KeeperTeams.fetchKeeperData] Starting keeper values API fetch');
        
        const keeperResponse = await fetch(keeperUrl, { headers });
        
        // console.log('[KeeperTeams.fetchKeeperData] Keeper values API response:', {
        //   status: keeperResponse.status,
        //   ok: keeperResponse.ok,
        //   url: keeperUrl
        // });
        
        if (keeperResponse.ok) {
          const keeperResponseData = await keeperResponse.json();
          
          // Handle different possible response structures
          if (keeperResponseData.season) {
            setAdpSeason(keeperResponseData.season);
          }

          if (keeperResponseData.keeper_values && Array.isArray(keeperResponseData.keeper_values)) {
            keeperResults = keeperResponseData.keeper_values;
          } else if (keeperResponseData.results && Array.isArray(keeperResponseData.results)) {
            keeperResults = keeperResponseData.results;
          } else if (Array.isArray(keeperResponseData)) {
            keeperResults = keeperResponseData;
          }
          
          setKeeperData(keeperResults);
          setCacheItem(keeperValuesCacheKey, keeperResults, ONE_DAY);
        } else {
          setKeeperData([]);
          setCacheItem(keeperValuesCacheKey, [], ONE_DAY);
        }
      } catch (keeperError) {
        setKeeperData([]);
        setCacheItem(keeperValuesCacheKey, [], ONE_DAY);
      }

      // Fetch player data from main API to get player names and details INCLUDING ADP
      let playersLookupData = {};
      try {
        const getApiBaseUrl = () => {
          if (process.env.NODE_ENV === "production") {
            return "https://weez-nfl-data.vercel.app";
          }
          return "";
        };

        // console.log('[KeeperTeams.fetchKeeperData] Starting players lookup API fetch');
        const playersResponse = await fetch(
          `${getApiBaseUrl()}/api/players/?live_draft=true&scoring_type=${selectedScoringType.includes('ppr') ? 'ppr' : selectedScoringType.includes('half') ? 'half_ppr' : 'standard'}`,
          { headers: getApiHeaders() }
        );
        
        // console.log('[KeeperTeams.fetchKeeperData] Players lookup API response:', {
        //   status: playersResponse.status,
        //   ok: playersResponse.ok
        // });
        
        if (playersResponse.ok) {
          const playersData = await playersResponse.json();
          // Store players data for lookup
          (playersData.results || []).forEach(player => {
            const sleeperId = player.external_ids?.sleeper || player.external_ids?.sleeper_id || player.sleeper_id;
            if (sleeperId) {
              playersLookupData[sleeperId] = player;
            }
          });
          setCacheItem(playersCacheKey, playersLookupData, ONE_DAY);
        } else {
          setCacheItem(playersCacheKey, {}, ONE_DAY);
        }
      } catch (playersError) {
        setCacheItem(playersCacheKey, {}, ONE_DAY);
      }

      // Store globally for access in other components
      window.playersLookup = playersLookupData;

      // console.log('[KeeperTeams.fetchKeeperData] API fetch summary:', {
      //   league: !!enhancedLeagueData,
      //   users: Object.keys(usersMap).length,
      //   rosters: rostersData.length,
      //   keeperPlayers: keeperResults.length,
      //   playersLookup: Object.keys(playersLookupData).length
      // });

    } catch (err) {
      console.error('[KeeperTeams.fetchKeeperData] Error during fetch process:', err);
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  // Function to clear all cached data for this league and scoring type
  const clearCache = () => {
    // console.log('[KeeperTeams.clearCache] Clearing cached data');
    if (!selectedScoringType) return;
    
    const cacheKeyBase = `keeper-data-${leagueId}-${selectedScoringType}`;
    const cacheKeys = [
      `${cacheKeyBase}-league`,
      `${cacheKeyBase}-users`,
      `${cacheKeyBase}-rosters`,
      `${cacheKeyBase}-keeper-values`,
      `${cacheKeyBase}-players`
    ];
    
    cacheKeys.forEach(key => removeCacheItem(key));
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchKeeperData();
      setLoading(false);
    };

    if (leagueId && user?.id && selectedScoringType) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, user?.id, selectedScoringType]);

  // Early return if colors are not properly initialized
  if (!colors || !colors.primary) {
    return null;
  }

  const handleBackClick = () => {
    navigate(`/leagues/${leagueId}`);
  };

  const handleKeeperSettingsClick = () => {
    navigate(`/leagues/${leagueId}/keeper-settings`);
  };

  const handleRefresh = () => {
    // console.log('[KeeperTeams.handleRefresh] Refreshing keeper data');
    clearCache();
    fetchKeeperData();
  };

  // Create roster players data by combining rosters with any available keeper data
  const rosterPlayersByOwner = rosters.reduce((acc, roster) => {
    const ownerId = roster.owner_id;
    if (!acc[ownerId]) {
      acc[ownerId] = [];
    }
    
    // Convert player IDs to player objects with actual player data lookup
    const rosterPlayers = (roster.players || []).map((playerId) => {
      // Look up player data from our lookup (playerId is the sleeper ID)
      const playerData = window.playersLookup?.[playerId] || { sleeper_id: playerId };
      
      // Find corresponding keeper data for this player using sleeper_id
      const playerKeeperData = keeperData.find(k => k.sleeper_id === playerId);

      // Combine player data with keeper data
      const combinedPlayer = {
        ...playerData,
        ...playerKeeperData,
        sleeper_id: playerId, // Ensure sleeper_id is consistent
        player_id: playerId, // Keep for backwards compatibility
      };
      
      return combinedPlayer;
    });
    
    acc[ownerId] = rosterPlayers;
    return acc;
  }, {});
  
  // Get all owners (from rosters, not just users, in case there's a mismatch)
  const allOwners = [...new Set(rosters.map(roster => roster.owner_id))].filter(id => id);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} sx={{ color: colors.blueAccent[500] }} />
        <Typography variant="h6" sx={{ color: colors.grey[300] }}>
          Loading keeper data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ mb: 3 }}>
          <IconButton
            onClick={handleBackClick}
            sx={{ color: colors.grey[300], mb: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
        <Alert 
          severity="error" 
          sx={{ 
            backgroundColor: colors.redAccent[800],
            color: colors.redAccent[100],
            '& .MuiAlert-icon': {
              color: colors.redAccent[300],
            },
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load keeper data
          </Typography>
          <Typography variant="body2">
            {error}. Please check your settings and try again.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4, flex: 1 }}>
      {/* Navigation */}
      <Box sx={{ mb: 3 }}>
        <IconButton
          onClick={handleBackClick}
          sx={{ 
            color: colors.grey[300], 
            mb: 2,
            '&:hover': {
              backgroundColor: colors.primary[600],
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Breadcrumbs sx={{ color: colors.grey[400] }}>
          <Link
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ 
              cursor: 'pointer',
              '&:hover': { color: colors.blueAccent[400] }
            }}
          >
            Dashboard
          </Link>
          <Link
            color="inherit"
            onClick={handleBackClick}
            sx={{ 
              cursor: 'pointer',
              '&:hover': { color: colors.blueAccent[400] }
            }}
          >
            {leagueDetails?.name || 'League'}
          </Link>
          <Typography color={colors.grey[100]}>
            Keeper Values
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Page Header */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          backgroundColor: colors.primary[400],
          border: `1px solid ${colors.grey[700]}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <EmojiEventsIcon
            sx={{
              fontSize: 40,
              color: colors.blueAccent[500],
              mr: 2,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                mb: 1,
              }}
            >
              Keeper Values
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: colors.grey[300],
                fontWeight: 400,
              }}
            >
              {leagueDetails?.name}{adpSeason ? ` - ${adpSeason} Season` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleRefresh}
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={refreshing}
              sx={{
                borderColor: colors.grey[500],
                color: colors.grey[300],
                textTransform: "uppercase",
                fontWeight: 600,
                "&:hover": {
                  borderColor: colors.grey[400],
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              onClick={handleKeeperSettingsClick}
              variant="outlined"
              startIcon={<SettingsIcon />}
              sx={{
                borderColor: colors.blueAccent[500],
                color: colors.blueAccent[500],
                textTransform: "uppercase",
                fontWeight: 600,
                "&:hover": {
                  borderColor: colors.blueAccent[400],
                  backgroundColor: "rgba(104,112,250,0.08)",
                },
              }}
            >
              Keeper Settings
            </Button>
          </Box>
        </Box>

        {/* Configuration Controls */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Scoring Type Chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                fontWeight: 500,
              }}
            >
              Scoring:
            </Typography>
            <Chip
              label={selectedScoringType?.replace('_', ' ').toUpperCase() || 'PPR'}
              size="small"
              sx={{
                backgroundColor: colors.greenAccent[800],
                color: colors.greenAccent[100],
                border: `1px solid ${colors.greenAccent[500]}`,
                fontWeight: 600,
              }}
            />
          </Box>

          {/* League Settings Chips */}
          {leagueDetails && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.grey[300],
                    fontWeight: 500,
                  }}
                >
                  Max Round:
                </Typography>
                <Chip
                  label={leagueDetails.keeper_highest_round || 'N/A'}
                  size="small"
                  sx={{
                    backgroundColor: colors.yellowAccent[800],
                    color: colors.yellowAccent[100],
                    border: `1px solid ${colors.yellowAccent[500]}`,
                    fontWeight: 600,
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.grey[300],
                    fontWeight: 500,
                  }}
                >
                  Round Cost:
                </Typography>
                <Chip
                  label={`+${leagueDetails.keeper_round_cost || 2}`}
                  size="small"
                  sx={{
                    backgroundColor: colors.redAccent[800],
                    color: colors.redAccent[100],
                    border: `1px solid ${colors.redAccent[500]}`,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                fontWeight: 500,
              }}
            >
              Season:
            </Typography>
            <Chip
              label={adpSeason ?? "—"}
              size="small"
              sx={{
                backgroundColor: colors.blueAccent[800],
                color: colors.blueAccent[100],
                border: `1px solid ${colors.blueAccent[500]}`,
                fontWeight: 600,
              }}
            />
          </Box>

          <Chip
            label={`${keeperData.length} Total Players`}
            sx={{
              backgroundColor: colors.blueAccent[800],
              color: colors.blueAccent[100],
              border: `1px solid ${colors.blueAccent[500]}`,
            }}
          />
        </Box>
      </Paper>

      {/* Teams Grid */}
      {allOwners.length > 0 ? (
        <Grid container spacing={3}>
          {allOwners.map((ownerId) => {
            const owner = users[ownerId];
            const rosterPlayers = rosterPlayersByOwner[ownerId] || [];
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={ownerId}>
                <KeeperTeamTile
                  owner={owner}
                  keeperPlayers={rosterPlayers}
                  leagueSettings={leagueDetails}
                  onClick={() => setSelectedTeamModal({ owner, keeperPlayers: rosterPlayers })}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.grey[700]}`,
            borderRadius: 2,
          }}
        >
          <EmojiEventsIcon
            sx={{
              fontSize: 60,
              color: colors.grey[600],
              mb: 2,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: colors.grey[200],
              mb: 1,
            }}
          >
            No Teams Found
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.grey[400],
            }}
          >
            Unable to load team information for this league.
          </Typography>
        </Paper>
      )}
      </Container>

      {/* Keeper Team Detail Modal */}
      <KeeperTeamDetailModal
        open={!!selectedTeamModal}
        onClose={() => setSelectedTeamModal(null)}
        owner={selectedTeamModal?.owner}
        keeperPlayers={selectedTeamModal?.keeperPlayers || []}
        leagueSettings={leagueDetails}
      />
    </Box>
  );
};

export default KeeperTeams;
